import { athenaQuery, COUNTRY, milesToKm } from '../../utils/AthenaApi';
import { getCinemasWithShowtimes } from '../../utils/GetCinemas';
import { getBestPoster, getBestTitle } from '../../utils/GetLocalizedMovies';
import { formatShowtimes } from './searchCinemasAndShowtimes';
import { getNextWeekDate, getFromDate } from '../../utils/dates';
import { getShowtimeTags } from '../../utils/tags';

const apiLimit = 120;

const toDate = getNextWeekDate();
const fromDate = getFromDate();

export const searchFilms = async (request: any, locationRadius: string) => {
  let searchTerm: string | null = null;
  let location: { lat: number; lon: number } | null = null;
  let maxDistanceKm: number | null = null;

  const showtimeTags = request.screeningType
    ? getShowtimeTags(request.screeningType)
    : [];

  if (request && request.search) {
    searchTerm = request.search;
  }

  if (request && request.location) {
    location = { lat: request.location.lat, lon: request.location.lon };
    maxDistanceKm = milesToKm(parseInt(locationRadius, 10));
  }

  // Find the films matching the title (a fuzzy search — there can be dozens).
  const films = await getFilms({
    offset: 0,
    film: request.film,
  });

  if (films.length === 0) {
    return [];
  }

  const filmIds = new Set(films.map((item: searchType) => item.node.node.id));

  // Fetch the scoped cinemas AND their schedules in one paginated query (the
  // film-finder always supplies a location, so the cinema set is bounded), then
  // keep only the showtimes whose film is in the searched set.
  const cinemas = await getCinemasWithShowtimes({
    apiLimit,
    location,
    maxDistanceKm,
    searchTerm,
    fromDate,
    toDate,
    showtimeTags,
  });

  return cinemas
    .map((cinema) => {
      const showtimesData = cinema.films
        .filter((result) => filmIds.has(result.movie.id))
        .map((result) => {
          const { movie, showtimes } = result;
          return {
            movie: {
              localePoster: {
                url: getBestPoster(movie),
              },
              localeTitle: getBestTitle(movie),
              localeCertificate:
                movie.mainRelease && movie.mainRelease.certificate
                  ? movie.mainRelease.certificate
                  : 'tbc',
            },
            showtimesFormatted: formatShowtimes(showtimes),
          };
        });

      if (showtimesData.length === 0) {
        return null;
      }

      return {
        id: cinema.id,
        name: cinema.name,
        url: cinema.url,
        coordinates: cinema.coordinates,
        showtimesData,
      };
    })
    .filter((cinema) => cinema !== null)
    .sort((a: any, b: any) => a.name.localeCompare(b.name, 'en'));
};

type searchParameters = {
  offset: number;
  film: string;
};

type searchType = {
  node: {
    node: {
      id: string;
      stringValue: string;
    };
  };
};

const searchLimit = 100;

const getFilms = (parameters: searchParameters): Promise<Array<searchType>> => {
  // Athena `search.movies` returns a Movie | CustomMovie union; we keep only
  // Movie and re-wrap into the legacy { node: { node: { id, stringValue } } }
  // shape the caller (item.node.node.id) expects. IDs are plain numeric strings
  // matched against each cinema's schedule — no base64 decode.
  const query = `
        query GetFilms {
            search {
                movies(
                    search: "${parameters.film}",
                    iso_3166_1_a2: "${COUNTRY}",
                    limit: ${searchLimit},
                    offset: ${parameters.offset}
                ) {
                    totalCount
                    nodes {
                        __typename
                        ... on Movie {
                            id
                            title
                        }
                    }
                }
            }
        }
    `;

  return new Promise((resolve) => {
    athenaQuery(query, 'search').then(async (r: any) => {
      if (r === null) {
        resolve([]);
        return;
      }

      const { totalCount, nodes } = r.data.search.movies;

      let results: Array<searchType> = nodes
        .filter((node: any) => node.__typename === 'Movie')
        .map((node: any) => ({
          node: { node: { id: node.id, stringValue: node.title } },
        }));

      if (parameters.offset + searchLimit < totalCount) {
        const paginatedResults = await getFilms({
          film: parameters.film,
          offset: parameters.offset + searchLimit,
        });

        results = [...results, ...paginatedResults];
      }

      resolve(results);
    });
  });
};
